/**
 * QUIPWA — CollectionDBService
 *
 * Reads and edits card content (front/back text, note type) in a deck's
 * `collection.db` — the database inside every `.wflash` file.
 *
 * Each deck instance has exactly one open collection database per page,
 * persisted on this device under `QUIPWA:collection-<deckInstanceId>` and
 * shared through the registry at the bottom of this file. Sharing matters:
 * two adapters on the same key used to overwrite each other's flushes, and
 * every "add card from note" opened a new one with its own timer that was
 * never closed.
 */

import { v4 as uuidv4 } from 'uuid'
import JSZip from 'jszip'
import type { IDBAdapter } from './db-adapter.ts'
import { openCollectionDB } from './db-factory.ts'

export interface CardContent {
  card_id: string
  note_id: string
  card_type: number
  template_ord: number
  front: string
  back: string
}

const CARD_CONTENT_SELECT = `
  SELECT
    c.id          AS card_id,
    c.note_id,
    c.card_type,
    c.template_ord,
    nf0.value     AS front,
    nf1.value     AS back
  FROM cards c
  JOIN note_fields nf0 ON nf0.note_id = c.note_id AND nf0.field_ord = 0
  JOIN note_fields nf1 ON nf1.note_id = c.note_id AND nf1.field_ord = 1
`

// ─── SERVICE ─────────────────────────────────────────────────────────────────

export class CollectionDBService {
  private readonly db: IDBAdapter

  constructor(db: IDBAdapter) {
    this.db = db
  }

  /** Get front/back content for a single card */
  async getCardContent(cardId: string): Promise<CardContent | null> {
    return this.db.queryOne<CardContent>(`${CARD_CONTENT_SELECT} WHERE c.id = ?`, [cardId])
  }

  /** Get content for multiple cards at once */
  async getCardContents(cardIds: string[]): Promise<CardContent[]> {
    if (cardIds.length === 0) return []
    const placeholders = cardIds.map(() => '?').join(',')
    return this.db.query<CardContent>(`${CARD_CONTENT_SELECT} WHERE c.id IN (${placeholders})`, cardIds)
  }

  /** All cards with content for a deck (keyed by the manifest deck_id). */
  async getAllCardContents(deckId: string): Promise<CardContent[]> {
    return this.db.query<CardContent>(`${CARD_CONTENT_SELECT} WHERE c.deck_id = ? ORDER BY c.created_at ASC`, [deckId])
  }

  /** Total card count */
  async getCardCount(): Promise<number> {
    return (await this.db.queryOne<{ count: number }>('SELECT COUNT(*) AS count FROM cards'))?.count ?? 0
  }

  /** Media rows referenced by the collection (their bytes live only in the .wflash). */
  async getMediaCount(): Promise<number> {
    return (await this.db.queryOne<{ count: number }>('SELECT COUNT(*) AS count FROM media'))?.count ?? 0
  }

  /** Update the text of an existing card. */
  async updateCard(cardId: string, front: string, back: string): Promise<void> {
    await this.db.transaction(async (db) => {
      const card = await db.queryOne<{ note_id: string }>('SELECT note_id FROM cards WHERE id = ?', [cardId])
      if (!card) return
      await db.run('UPDATE note_fields SET value = ? WHERE note_id = ? AND field_ord = 0', [front, card.note_id])
      await db.run('UPDATE note_fields SET value = ? WHERE note_id = ? AND field_ord = 1', [back, card.note_id])
      await db.run('UPDATE notes SET updated_at = ? WHERE id = ?', [Math.floor(Date.now() / 1000), card.note_id])
    })
  }

  /** Create a basic card (note + fields + card). Returns the card id. */
  async createCard(deckId: string, front: string, back: string): Promise<string> {
    const noteId = uuidv4()
    const cardId = uuidv4()
    const now = Math.floor(Date.now() / 1000)

    await this.db.transaction(async (db) => {
      await db.run('INSERT INTO notes (id, deck_id, note_type, created_at, updated_at) VALUES (?, ?, 0, ?, ?)', [noteId, deckId, now, now])
      await db.run('INSERT INTO note_fields (note_id, field_ord, value) VALUES (?, 0, ?)', [noteId, front])
      await db.run('INSERT INTO note_fields (note_id, field_ord, value) VALUES (?, 1, ?)', [noteId, back])
      await db.run('INSERT INTO cards (id, note_id, deck_id, template_ord, card_type, created_at) VALUES (?, ?, ?, 0, 0, ?)', [cardId, noteId, deckId, now])
    })

    return cardId
  }

  /** Delete a card and its note. */
  async deleteCard(cardId: string): Promise<void> {
    await this.deleteCards([cardId])
  }

  /** Delete several cards and their notes. */
  async deleteCards(cardIds: string[]): Promise<void> {
    if (cardIds.length === 0) return
    const placeholders = cardIds.map(() => '?').join(',')
    await this.db.transaction(async (db) => {
      const cards = await db.query<{ note_id: string }>(`SELECT DISTINCT note_id FROM cards WHERE id IN (${placeholders})`, cardIds)
      const noteIds = cards.map((c) => c.note_id)
      if (noteIds.length > 0) {
        const notePlaceholders = noteIds.map(() => '?').join(',')
        await db.run(`DELETE FROM notes WHERE id IN (${notePlaceholders})`, noteIds)
        await db.run(`DELETE FROM note_fields WHERE note_id IN (${notePlaceholders})`, noteIds)
      }
      await db.run(`DELETE FROM cards WHERE id IN (${placeholders})`, cardIds)
    })
  }

  /** Serialize the whole collection (to package as a .wflash for Drive). */
  async exportBuffer(): Promise<ArrayBuffer> {
    return this.db.exportToArrayBuffer()
  }

  /** Replace the whole collection with the `collection.db` inside a .wflash. */
  async replaceFromWflash(wflashBuffer: ArrayBuffer): Promise<void> {
    await this.db.importFromArrayBuffer(await extractCollectionDB(wflashBuffer))
  }

  /** Run several statements atomically (bulk inserts, seeding). */
  async transaction(fn: (db: IDBAdapter) => Promise<void>): Promise<void> {
    await this.db.transaction(fn)
  }

  /** Persist pending writes now. */
  async flush(): Promise<void> {
    await this.db.flushNow?.()
  }

  async close(): Promise<void> {
    await this.db.close()
  }
}

/** Pull `collection.db` out of a .wflash ZIP. */
export async function extractCollectionDB(wflashBuffer: ArrayBuffer): Promise<ArrayBuffer> {
  const zip = await JSZip.loadAsync(wflashBuffer)
  const dbFile = zip.file('collection.db')
  if (!dbFile) throw new Error('collection.db not found in .wflash')
  return dbFile.async('arraybuffer')
}

// ─── REGISTRY: one open collection per deck instance ─────────────────────────

const registry = new Map<string, Promise<CollectionDBService>>()

/**
 * The deck's collection, opened from its local copy (empty if there is none
 * yet). Every caller for the same deck gets the same instance.
 */
export function getCollection(deckInstanceId: string): Promise<CollectionDBService> {
  let pending = registry.get(deckInstanceId)
  if (!pending) {
    pending = openCollectionDB(deckInstanceId).then(({ adapter }) => new CollectionDBService(adapter))
    pending.catch(() => registry.delete(deckInstanceId))
    registry.set(deckInstanceId, pending)
  }
  return pending
}

/**
 * Open a deck's collection from a downloaded .wflash. If this device already
 * holds content for the deck, that copy is returned as-is (refreshing it from
 * Drive is the sync engine's job, not the reader's).
 */
export async function loadCollectionDB(deckInstanceId: string, wflashBuffer: ArrayBuffer): Promise<CollectionDBService> {
  const service = await getCollection(deckInstanceId)
  if ((await service.getCardCount()) === 0) await service.replaceFromWflash(wflashBuffer)
  return service
}

/**
 * The local copy of a deck's collection, or null when this device has no
 * content for it — so callers can tell "nothing here" from "an empty deck".
 */
export async function openLocalCollectionDB(deckInstanceId: string): Promise<CollectionDBService | null> {
  const service = await getCollection(deckInstanceId)
  return (await service.getCardCount()) === 0 ? null : service
}

/** Close and forget a deck's collection (after deleting the deck). */
export async function evictCollectionCache(deckInstanceId: string): Promise<void> {
  const pending = registry.get(deckInstanceId)
  registry.delete(deckInstanceId)
  if (pending) await (await pending).close()
}

/** Close every open collection (sign-out, tests). */
export async function closeAllCollections(): Promise<void> {
  const all = [...registry.values()]
  registry.clear()
  await Promise.all(all.map(async (p) => (await p).close()))
}
